import { useEffect } from 'react';

import { CloseIcon, VideoRecorderIcon } from '@braskai/icons';

import { useClientStorageFlag } from '@/entities/clientStorage';
import { useScreenRecordAction } from '@/entities/recording';

import { TranslationKey, useInterfaceLanguage } from '@/shared/i18n';
import { TRIAL_RECORDING_MAX_SECONDS } from '@/shared/lib/billing/trialRecording';
import { MixpanelEvents } from '@/shared/thirdParty/mixpanel';

import { RECORDING_INTRO_STORAGE_KEY } from '../../lib/constants';
import { RecordingIntroVariant } from '../../lib/getRecordingIntroVariant';
import { useRecordingIntroStore } from '../../model/useRecordingIntroStore';
import {
  SBadge,
  SBadgeDot,
  SCameraBubble,
  SCard,
  SClickTarget,
  SCloseButton,
  SCtaButton,
  SCursor,
  SDescription,
  SDialog,
  SPreview,
  SRecBadge,
  SRecDot,
  SRipple,
  SSkeleton,
  SSkeletonLine,
  STitle,
  SWindow,
  SWindowBar,
  SZoomLayer,
} from './styled';

const TITLE_ID = 'recording-intro-title';
const INTRO_SOURCE = 'new_recording';
const PREVIEW_RECORDING_TIME = '00:12';

type IntroCopy = {
  // A plan badge above the title; a variant without one starts with the title.
  badge?: TranslationKey;
  description: TranslationKey;
};

const INTRO_COPY: Record<RecordingIntroVariant, IntroCopy> = {
  paid: {
    description: 'recordingModals.intro.paid.description',
  },
  trial: {
    badge: 'recordingModals.intro.trial.badge',
    description: 'recordingModals.intro.trial.description',
  },
};

const IntroPreview = () => {
  const { t } = useInterfaceLanguage();

  return (
    <SPreview aria-hidden="true" data-testid="recording-intro-preview">
      <SWindow>
        <SZoomLayer>
          <SWindowBar>
            <span />
            <span />
            <span />
          </SWindowBar>
          <SSkeleton>
            <SSkeletonLine $tone={200} $width="52%" />
            <SSkeletonLine $tone={100} $width="78%" />
            <SSkeletonLine $tone={100} $width="64%" />
          </SSkeleton>
          <SClickTarget>{t('recordingModals.paywall.autoZoom.preview.cta')}</SClickTarget>
        </SZoomLayer>
        <SRipple />
        <SCursor fill="none" height={20} viewBox="0 0 24 24" width={20}>
          <path d="M5 3l14 8-6 1.5L11 19 5 3z" strokeLinejoin="round" strokeWidth="1.2" />
        </SCursor>
      </SWindow>
      <SCameraBubble>
        <VideoRecorderIcon height={20} width={20} />
      </SCameraBubble>
      <SRecBadge>
        <SRecDot />
        {t('recordingModals.paywall.recordingMinutes.preview.rec', { time: PREVIEW_RECORDING_TIME })}
      </SRecBadge>
    </SPreview>
  );
};

/**
 * Shown once per user of a trial or paid space in place of the recorder on their first "new recording" (see
 * useRecordingIntroGate): explains what Rask does with a recording. "Record video" continues to the recorder; closing it does not. Either way the
 * intro is not shown again.
 */
export const RecordingIntroModal = () => {
  const { t } = useInterfaceLanguage();
  const isOpen = useRecordingIntroStore((state) => state.isOpen);
  const variant = useRecordingIntroStore((state) => state.variant);
  const closeIntro = useRecordingIntroStore((state) => state.close);
  const openScreenRecord = useScreenRecordAction();
  // The gate has already read the flag; the modal only spends it.
  const { markAsShown } = useClientStorageFlag(RECORDING_INTRO_STORAGE_KEY, { enabled: false });

  useEffect(() => {
    if (isOpen) MixpanelEvents.recordingIntroPromptViewed({ source: INTRO_SOURCE, variant });
  }, [isOpen, variant]);

  if (!isOpen) return null;

  const copy = INTRO_COPY[variant];

  const handleClose = () => {
    MixpanelEvents.recordingIntroPromptDismiss({ source: INTRO_SOURCE, variant });
    markAsShown();
    closeIntro();
  };

  const handleRecord = () => {
    MixpanelEvents.recordingIntroRecordClick({ source: INTRO_SOURCE, variant });
    markAsShown();
    closeIntro();
    void openScreenRecord();
  };

  return (
    <SDialog aria-labelledby={TITLE_ID} data-testid="recording-intro" open onClose={handleClose}>
      <SCard>
        <SCloseButton
          aria-label={t('common.close')}
          data-testid="recording-intro-close"
          type="button"
          onClick={handleClose}
        >
          <CloseIcon height={18} width={18} />
        </SCloseButton>

        {copy.badge && (
          <SBadge data-testid="recording-intro-badge">
            <SBadgeDot />
            {t(copy.badge)}
          </SBadge>
        )}
        <STitle id={TITLE_ID}>{t('records.zeroState.title')}</STitle>

        <IntroPreview />

        <SDescription>{t(copy.description, { minutes: TRIAL_RECORDING_MAX_SECONDS / 60 })}</SDescription>

        <SCtaButton data-testid="recording-intro-record" variant="primary" fullWidth onClick={handleRecord}>
          <VideoRecorderIcon height={18} width={18} />
          {t('records.zeroState.action')}
        </SCtaButton>
      </SCard>
    </SDialog>
  );
};
